import { PostApiAuthUpdateTokensApiResponse } from "@entities/auth";
import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { LOCAL_STORAGE } from "@shared/constants";
import {
  API_PREFIX,
  API_URL,
  ApiResponse,
  CheckSentryEnable,
  CheckSentryExceptionsStatusCodes,
} from "@shared/index";
import { useSentryCaptureException } from "../hooks/useSentryCaptureException";

type BaseQueryWithReauth = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
>;

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem(LOCAL_STORAGE.RefreshToken);
      const sessionId = localStorage.getItem(LOCAL_STORAGE.SessionId);
      const telegramId = localStorage.getItem(LOCAL_STORAGE.TelegramId);
      const userId = localStorage.getItem(LOCAL_STORAGE.UserId);

      if (!refreshToken || !sessionId || !telegramId || !userId) {
        throw new Error("Missing authentication data");
      }

      const response = await fetch(
        `${API_URL}${API_PREFIX}/auth/update-tokens`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            refresh_token: refreshToken,
            session_id: sessionId,
            telegram_id: Number(telegramId),
            user_id: userId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to refresh token: ${response.statusText}`);
      }

      const {
        data,
        status,
        message,
      }: ApiResponse<PostApiAuthUpdateTokensApiResponse> =
        await response.json();

      if (status === "error") {
        throw new Error(`Failed to refresh token: ${message}`);
      }

      localStorage.setItem(
        LOCAL_STORAGE.AccessToken,
        data.access_token as string
      );
      localStorage.setItem(LOCAL_STORAGE.RefreshToken, data.refresh_token);
      localStorage.setItem(LOCAL_STORAGE.SessionId, data.session_id);
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function reauthorize(): Promise<boolean> {
  try {
    const initData = (window as any).Telegram?.WebApp?.initData;

    if (!initData) {
      throw new Error("No Telegram WebApp data available");
    }

    const response = await fetch(`${API_URL}${API_PREFIX}/auth/authorization`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        init_data: initData,
      }),
    });

    if (!response.ok) {
      throw new Error(`Reauthorization failed: ${response.statusText}`);
    }

    const { data, status } = await response.json();

    if (status === "error") {
      throw new Error("Reauthorization failed");
    }

    localStorage.setItem(LOCAL_STORAGE.AccessToken, data.access_token);
    localStorage.setItem(LOCAL_STORAGE.RefreshToken, data.refresh_token);
    localStorage.setItem(LOCAL_STORAGE.SessionId, data.session_id);
    localStorage.setItem(LOCAL_STORAGE.UserId, data.user_id);
    localStorage.setItem(LOCAL_STORAGE.TelegramId, String(data.telegram_id));

    return true;
  } catch (error) {
    return false;
  }
}

export const createBaseQueryWithReauth = (
  uri: string,
  onUnauthorized: () => void
): BaseQueryWithReauth => {
  const baseQuery = fetchBaseQuery({
    baseUrl: uri,
    prepareHeaders: (headers) => {
      const accessToken = localStorage.getItem(LOCAL_STORAGE.AccessToken);
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }
      return headers;
    },
  });

  return async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: any
  ) => {
    const maxRetries = extraOptions?.maxRetries ?? 0;
    let attempt = 0;
    let result = await baseQuery(args, api, extraOptions);

    while (
      result.error &&
      (result.error.status === 400 || result.error.status === 500) &&
      attempt < maxRetries
    ) {
      attempt++;
      console.log(
        `Retry attempt ${attempt}/${maxRetries} for ${
          typeof args === "string" ? args : args.url
        }`
      );
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      result = await baseQuery(args, api, extraOptions);
    }

    const is401 =
      result.error &&
      (result.error.status === 401 ||
        (result.error.status === "PARSING_ERROR" &&
          (result.error as any).originalStatus === 401));

    if (
      is401 &&
      typeof args !== "string" &&
      !args.url?.includes("/auth/authorization") &&
      !args.url?.includes("/auth/update-tokens")
    ) {
      try {
        await refreshAccessToken();

        result = await baseQuery(args, api, extraOptions);
      } catch (refreshError) {
        const reauthorized = await reauthorize();

        if (reauthorized) {
          result = await baseQuery(args, api, extraOptions);
        } else {
          localStorage.removeItem(LOCAL_STORAGE.AccessToken);
          localStorage.removeItem(LOCAL_STORAGE.RefreshToken);
          localStorage.removeItem(LOCAL_STORAGE.SessionId);
          localStorage.removeItem(LOCAL_STORAGE.UserId);
          localStorage.removeItem(LOCAL_STORAGE.TelegramId);

          if (!window.location.pathname.includes("/authError")) {
            onUnauthorized();
          }
        }
      }
    }

    if (result.error && result.error.status === 500) {
      // console.error("[500 Error] Redirecting to /unknownError", result.error);
      // if (!window.location.pathname.includes("/authError")) {
      //   window.location.href = "/unknownError";
      // }
      console.log("500 error >>>>", result.error);
    }

    if (
      result.error &&
      result.error.status !== 401 &&
      CheckSentryEnable() &&
      CheckSentryExceptionsStatusCodes(result)
    ) {
      useSentryCaptureException(result.error);
    }

    return result;
  };
};
